// src/hooks/useAuthState.ts
import { useReducer, useCallback } from "react";
import { Session } from "@supabase/supabase-js";
import { UserSession } from "../types/database.enhanced";

interface AuthState {
  user: UserSession | null;
  session: Session | null;
  isLoading: boolean;
  isInitialized: boolean;
  error: Error | null;
  features: Record<string, boolean>;
}

type AuthAction =
  | { type: "SET_USER"; payload: UserSession | null }
  | { type: "SET_SESSION"; payload: Session | null }
  | { type: "SET_LOADING"; payload: boolean }
  | { type: "SET_INITIALIZED"; payload: boolean }
  | { type: "SET_ERROR"; payload: Error | null }
  | { type: "SET_FEATURES"; payload: Record<string, boolean> }
  | { type: "CLEAR_ERROR" }
  | { type: "RESET" };

const initialState: AuthState = {
  user: null,
  session: null,
  isLoading: true,
  isInitialized: false,
  error: null,
  features: {},
};

function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case "SET_USER":
      return { ...state, user: action.payload };
    case "SET_SESSION":
      return { ...state, session: action.payload };
    case "SET_LOADING":
      return { ...state, isLoading: action.payload };
    case "SET_INITIALIZED":
      return { ...state, isInitialized: action.payload };
    case "SET_ERROR":
      return { ...state, error: action.payload };
    case "SET_FEATURES":
      return { ...state, features: action.payload };
    case "CLEAR_ERROR":
      return { ...state, error: null };
    case "RESET":
      return initialState;
    default:
      return state;
  }
}

export function useAuthState() {
  const [state, dispatch] = useReducer(authReducer, initialState);

  const setUser = useCallback((user: UserSession | null) => {
    dispatch({ type: "SET_USER", payload: user });
  }, []);

  const setSession = useCallback((session: Session | null) => {
    dispatch({ type: "SET_SESSION", payload: session });
  }, []);

  const setLoading = useCallback((isLoading: boolean) => {
    dispatch({ type: "SET_LOADING", payload: isLoading });
  }, []);

  const setInitialized = useCallback((isInitialized: boolean) => {
    dispatch({ type: "SET_INITIALIZED", payload: isInitialized });
  }, []);

  const setError = useCallback((error: Error | null) => {
    dispatch({ type: "SET_ERROR", payload: error });
  }, []);

  const setFeatures = useCallback((features: Record<string, boolean>) => {
    dispatch({ type: "SET_FEATURES", payload: features });
  }, []);

  const clearError = useCallback(() => {
    dispatch({ type: "CLEAR_ERROR" });
  }, []);

  const reset = useCallback(() => {
    dispatch({ type: "RESET" });
  }, []);

  return {
    state,
    setUser,
    setSession,
    setLoading,
    setInitialized,
    setError,
    setFeatures,
    clearError,
    reset,
  };
}
